import React from 'react';
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Scale, AlertTriangle, Shield, Info } from "lucide-react";

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="flex gap-2">
            <Link href="/privacy">
              <Button variant="outline" size="sm">
                Privacy Policy
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Tradeharbor.io" className="h-20 w-auto" />
          </div>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <Scale className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Terms of Use</h1>
          <p className="text-lg text-muted-foreground">
            Last Updated: February 10, 2026
          </p>
        </div>

        {/* Quick Nav */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <a href="#services" className="hover:text-primary transition-colors">Platform Services</a>
              <a href="#ai-features" className="hover:text-primary transition-colors">AI Features</a>
              <a href="#risks" className="hover:text-primary transition-colors">Investment Risks</a>
              <a href="#arbitration" className="hover:text-primary transition-colors">Dispute Resolution</a>
              <a href="#contact" className="hover:text-primary transition-colors">Contact Info</a>
              <a href="#disclosures" className="hover:text-primary transition-colors">Disclosures</a>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          
          <section>
            <h2>Acceptance of Terms</h2>
            <p>
              By accessing or using Tradeharbor.io ("Platform", "Service", "we", "us", "our"), you
              ("User", "you", "your") agree to be bound by these Terms of Use. If you do not agree
              to these terms, do not use the Platform.
            </p>
            <p>
              Tradeharbor.io operates a financial technology platform providing links to services of
              third-party vendors, including but not limited to Alpaca Markets, Inc. ("Alpaca"), and
              AI-powered trading analysis tools. These third-party services are not under the control
              of Tradeharbor.io.
            </p>
          </section>

          <section id="services">
            <h2>1. Platform Services</h2>
            
            <h3>1.1 Services Provided</h3>
            <p>Tradeharbor.io provides:</p>
            <ul>
              <li>Access to third-party brokerage services through Alpaca Markets</li>
              <li>AI-powered sentiment analysis and trading insights powered by Claude (Anthropic)</li>
              <li>Portfolio management and tracking tools</li>
              <li>Market data and real-time quotes</li>
              <li>Educational resources and trading tools</li>
            </ul>

            <h3>1.2 Third-Party Services</h3>
            <p>
              All securities trading and brokerage services are provided by Alpaca Markets, Inc., a
              registered broker-dealer and member of FINRA/SIPC. Tradeharbor.io acts solely as a
              technology platform connecting users to these services.
            </p>
          </section>

          <section>
            <h2>2. Account Eligibility and Registration</h2>
            
            <h3>2.1 Eligibility Requirements</h3>
            <p>To use Tradeharbor.io, you must:</p>
            <ul>
              <li>Be at least 18 years of age</li>
              <li>Be a legal resident of the United States</li>
              <li>Have the legal capacity to enter into binding contracts</li>
              <li>Not be prohibited from trading securities under applicable law</li>
              <li>Provide accurate and complete registration information</li>
            </ul>

            <h3>2.2 Account Security</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
              <li>Ensuring your account information is current and accurate</li>
            </ul>
          </section>

          <section>
            <h2>3. Brokerage Services and Trading</h2>
            
            <h3>3.1 Brokerage Relationship</h3>
            <p>
              Securities trading services are provided by Alpaca Markets, Inc. By using
              Tradeharbor.io to access these services, you acknowledge that:
            </p>
            <ul>
              <li>You have read and agree to Alpaca's Customer Agreement</li>
              <li>Alpaca, not Tradeharbor.io, is your broker-dealer</li>
              <li>All trades are executed through Alpaca's systems</li>
              <li>Tradeharbor.io is not a registered broker-dealer</li>
            </ul>

            <h3>3.2 Trading Authorization</h3>
            <p>By connecting your Alpaca account to Tradeharbor.io, you authorize us to:</p>
            <ul>
              <li>Submit trading orders to Alpaca on your behalf</li>
              <li>Access your account information from Alpaca</li>
              <li>Display your positions, balances, and trading history</li>
              <li>Facilitate AI-powered trading analysis</li>
            </ul>
          </section>

          <section id="ai-features">
            <h2>4. AI-Powered Features and Analysis</h2>
            
            <h3>4.1 Sentiment Analysis</h3>
            <p>Our AI-powered sentiment analysis tool:</p>
            <ul>
              <li>Analyzes news and market data using artificial intelligence (Claude by Anthropic)</li>
              <li>Provides sentiment scores and insights</li>
              <li>Is for informational purposes only</li>
              <li>Does not constitute investment advice</li>
            </ul>

            <h3>4.2 No Investment Advice</h3>
            <Card className="border-yellow-500/50 bg-yellow-500/10 my-4">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="font-semibold text-sm mb-2">IMPORTANT: Not Investment Advice</p>
                    <p className="text-sm">
                      Tradeharbor.io and its AI features do NOT provide personalized investment advice,
                      do NOT recommend specific securities or trading strategies, are NOT a substitute 
                      for professional financial advice, and should NOT be relied upon as the sole basis 
                      for investment decisions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3>4.3 AI Limitations</h3>
            <p>You acknowledge that:</p>
            <ul>
              <li>AI analysis may contain errors or inaccuracies</li>
              <li>Past performance does not guarantee future results</li>
              <li>Sentiment scores are predictive models, not certainties</li>
              <li>AI tools are provided "as is" without warranties</li>
            </ul>
          </section>

          <section id="risks">
            <h2>5. Investment Risks and Disclaimers</h2>
            
            <Card className="border-red-500/50 bg-red-500/10 my-6">
              <CardContent className="py-6">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                  <div>
                    <p className="font-bold text-lg mb-2">WARNING: Substantial Risk of Loss</p>
                    <p className="text-sm">
                      Trading securities involves substantial risk of loss. You may lose some or all of 
                      your invested capital. Securities markets are volatile and unpredictable.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3>5.1 Investment Risks</h3>
            <p>You acknowledge and understand:</p>
            <ul>
              <li>You may lose some or all of your invested capital</li>
              <li>Past performance is not indicative of future results</li>
              <li>Securities markets are volatile and unpredictable</li>
              <li>Margin trading amplifies both gains and losses</li>
            </ul>
          </section>

          <section>
            <h2>6. Fees and Charges</h2>
            <p>
              Tradeharbor.io may charge subscription fees for premium features. Trading commissions 
              and fees are charged by Alpaca Markets according to their fee schedule.
            </p>
          </section>

          <section id="arbitration">
            <h2>12. Dispute Resolution and Arbitration</h2>
            
            <Card className="border-orange-500/50 bg-orange-500/10 my-4">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 mt-1" />
                  <div className="text-sm">
                    <p className="font-semibold mb-2">IMPORTANT: PLEASE READ CAREFULLY</p>
                    <p>
                      This section affects your legal rights, including your right to file a lawsuit in court.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3>12.1 Mandatory Arbitration</h3>
            <p>
              Any dispute shall be resolved by binding arbitration administered by FINRA Dispute Resolution 
              or AAA, rather than in court.
            </p>

            <h3>12.2 Class Action Waiver</h3>
            <p>
              Claims may only be brought in your individual capacity, not as a class member.
            </p>

            <h3>12.3 Opt-Out Right</h3>
            <p>
              You may opt out within 30 days by emailing: <a href="mailto:legal@tradeharbor.io" className="text-primary hover:underline">legal@tradeharbor.io</a>
            </p>
          </section>

          <section id="contact">
            <h2>17. Contact Information</h2>
            
            <div className="not-prose">
              <Card>
                <CardContent className="py-4 space-y-3">
                  <div>
                    <p className="font-semibold">Tradeharbor.io Support</p>
                    <p className="text-sm text-muted-foreground">
                      Email: <a href="mailto:support@tradeharbor.io" className="text-primary hover:underline">support@tradeharbor.io</a>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Legal: <a href="mailto:legal@tradeharbor.io" className="text-primary hover:underline">legal@tradeharbor.io</a>
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Regulatory Information
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Brokerage services by <strong>Alpaca Markets, Inc.</strong> - Member FINRA/SIPC<br />
                      <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        https://alpaca.markets
                      </a>
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">SIPC Protection</p>
                    <p className="text-sm text-muted-foreground">
                      Securities protected up to $500,000 ($250,000 cash)<br />
                      <a href="https://www.sipc.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.sipc.org</a> | (202) 371-8300
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <section id="disclosures">
            <h2>18. Important Disclosures</h2>
            
            <Card className="border-red-500/50 bg-red-500/10 my-6">
              <CardContent className="py-6 space-y-4">
                <div>
                  <h3 className="font-bold text-lg mb-2">Not Investment Advice</h3>
                  <p className="text-sm">
                    TRADEHARBOR.IO DOES NOT PROVIDE INVESTMENT ADVICE. All information is for educational purposes only.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-lg mb-2">Risk of Loss</h3>
                  <p className="text-sm">
                    SECURITIES TRADING INVOLVES SUBSTANTIAL RISK OF LOSS. You may lose all invested capital.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-lg mb-2">Past Performance</h3>
                  <p className="text-sm">
                    PAST PERFORMANCE IS NOT INDICATIVE OF FUTURE RESULTS.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          <Card className="border-green-500/50 bg-green-500/10 my-8">
            <CardContent className="py-6">
              <p className="text-center font-bold">
                BY USING TRADEHARBOR.IO, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE
                TO BE BOUND BY THESE TERMS OF USE.
              </p>
            </CardContent>
          </Card>

          <p className="text-sm text-muted-foreground text-center italic">
            These Terms of Use are effective as of February 10, 2026 and supersede all prior versions.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t bg-background/95 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <div className="flex gap-4">
              <Link href="/privacy">
                <a className="hover:text-primary transition-colors">Privacy Policy</a>
              </Link>
              <Link href="/terms">
                <a className="hover:text-primary transition-colors">Terms of Use</a>
              </Link>
              <Link href="/">
                <a className="hover:text-primary transition-colors">Dashboard</a>
              </Link>
            </div>
            <div>
              © 2026 Tradeharbor.io. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
